
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  DotsCircleHorizontalIcon,
  LogoutIcon,
  DeviceMobileIcon,
  SparklesIcon,
  ChevronLeftIcon,
} from "@heroicons/vue/outline";
import { useConversation } from "@/composables/conversation";
import { useMessengerHeader } from "@/composables/messengerHeader";
import { confirmDialog } from "@/dialogs/ConfirmDialog";
import { useChatConnection } from "@/composables/chatConnection";
import { useWidgetConfig } from "@/composables/widgetConfig";
import { showTransferConversationDialog } from "@/dialogs/TransferConversationDialog";
import { useApp } from "@/composables/use-app";
import { useWidgetTheme } from "@/composables/widgetTheme";
import { css } from "@emotion/css";
import { TinyColor } from "@ctrl/tinycolor";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "MessengerHeader",
  emits: ["minimize"],
  components: {
    DotsCircleHorizontalIcon,
    DeviceMobileIcon,
    ChevronLeftIcon,
    SparklesIcon,
    LogoutIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
  },
  setup() {
    const { useMobileLayout } = useApp();
    const { conversation } = useConversation();
    const { setHeight, backAction } = useMessengerHeader();
    const { requestEndConversation } = useChatConnection();
    const { canTransferToAnyChannel, configuration, branding } =
      useWidgetConfig();
    const { theme } = useWidgetTheme();
    const { t } = useI18n();

    const container = ref<HTMLDivElement>();

    const productWebsite = computed(() => branding.value?.productWebsite);

    const showTransferOption = computed(() => !!conversation.value?.isStarted);
    const isConversationStarted = computed(
      () => !!conversation.value?.isStarted
    );
    const isConversationTransferred = computed(
      () => !!conversation.value?.isTransferred
    );
    const isConversationEnded = computed(() => !!conversation.value?.isEnded);

    const canEndConversation = computed(
      () =>
        isConversationStarted.value &&
        !isConversationEnded.value &&
        !isConversationTransferred.value
    );

    const canTransfer = computed(
      () =>
        canTransferToAnyChannel.value &&
        !backAction.value &&
        !isConversationEnded.value &&
        !isConversationTransferred.value
    );

    watch(container, () => {
      if (container.value) {
        const clientHeight = container.value.clientHeight;
        setHeight(clientHeight);
      } else {
        console.warn("Could not get header container height");
      }
    });

    const endConversation = async () => {
      const confirm = await confirmDialog(
        t("messengerHeader.endConversationButtonText"),
        t("messengerHeader.endConversationConfirmationMessage")
      );

      if (confirm) {
        requestEndConversation();
      }
    };

    const initTransfer = async () => {
      showTransferConversationDialog();
    };

    const backgroundClass = computed(
      () => css`
        background-color: ${theme.value?.headerBackgroundColor};
        color: ${theme.value?.headerTextColor};
        --tw-shadow-color: ${theme.value?.headerBackgroundColor};
        --tw-shadow: var(--tw-shadow-colored);
      `
    );

    const hoverColor = computed(() => {
      const colorInfo = new TinyColor(theme.value?.headerTextColor);
      return "#" + colorInfo.brighten(20).toHex();
    });

    const textColorClass = computed(() => {
      return css`
        color: ${theme.value?.headerTextColor};

        &:hover {
          color: ${hoverColor.value};
        }

        // &.group:has(> :hover) {
        //   color: ${hoverColor.value};
        // }
      `;
    });

    return {
      showTransferOption,
      isConversationStarted,
      isConversationEnded,
      container,
      backAction,
      endConversation,
      canTransferToAnyChannel,
      canEndConversation,
      canTransfer,
      initTransfer,
      configuration,
      isMobile: useMobileLayout,
      backgroundClass,
      textColorClass,
      t,
      productWebsite,
    };
  },
});
